// src/components/home/HeroCarousel.jsx
import React, { useEffect, useState } from 'react';

/**
 * Hero Carousel
 *
 * Cross-fades a set of background images beneath fixed foreground content.
 * The headline and CTA never move — only the backdrop changes — so the hero
 * carries a single message rather than a rotating one.
 */
const SLIDE_MS = 6000;
const FADE_MS = 1200;

const HeroCarousel = ({ slides, children }) => {
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(false);
  // Every slide sits inside the viewport, so loading="lazy" would not defer
  // them. Hold the rest back until the first has painted instead, so they
  // don't compete with it for bandwidth.
  const [showRest, setShowRest] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setAnimate(!query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  // Anyone who has asked for reduced motion stays on the first slide.
  useEffect(() => {
    if (!animate || slides.length < 2) return;
    const timer = setInterval(
      () => setIndex((current) => (current + 1) % slides.length),
      SLIDE_MS
    );
    return () => clearInterval(timer);
  }, [animate, slides.length]);

  return (
    <section className="relative h-[80vh] flex items-center overflow-hidden bg-gray-950">
      <div className="absolute inset-0 z-0">
        {slides.map((slide, i) => (i === 0 || showRest) && (
          <img
            key={slide}
            src={slide}
            alt=""
            fetchpriority={i === 0 ? 'high' : 'low'}
            decoding="async"
            onLoad={i === 0 ? () => setShowRest(true) : undefined}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              opacity: i === index ? 1 : 0,
              // Slow pan across the slide's life; the zoom-out on the way
              // back to rest is hidden behind the fade.
              transform: animate && i === index ? 'scale(1.05)' : 'scale(1)',
              transition: `opacity ${FADE_MS}ms ease-in-out, transform ${SLIDE_MS + FADE_MS}ms linear`,
            }}
          />
        ))}
        {/* Constant scrim so the type never depends on any one image behaving.
            Narrow viewports crop to the middle of the frame rather than the
            art-directed dark left edge, so they get a flat wash instead. */}
        <div className="absolute inset-0 bg-black/45 md:bg-transparent md:bg-gradient-to-r md:from-black/85 md:via-black/50 md:to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {children}
      </div>
    </section>
  );
};

export default HeroCarousel;
